import type { AnalysisOrder } from "../redux/analysisOrder";
import type { AnalysisResultData, AnalysisResultSection } from "../../../services/AnalysisResultService";
import { definitionNeedsPreparation, getAnalysisDefinition } from "./analysisConfig";
import { normalizePreparationRoutingItem, normalizeSamples } from "./utils";
import { calculateMagneticResults, resolveCoContent } from "./analysisCalculations";

export const MECHANICAL_PREPARATION_KEY = "mechanicalPreparation";
export const CHIP_RESULT_ANALYSES = ["sem", "xrd", "icp", "lom", MECHANICAL_PREPARATION_KEY];
/** Manually entered σs row in the Hc / MM / Density section; always shown because CoM and MM need it. */
export const SIGMA_S_ROW = "σs";
const HC_MM_DENSITY_ROW_ORDER = ["Hc", SIGMA_S_ROW, "MM", "Density"];
export const GRADIENT_UNIT = "µm";
/** Per-sample fields stored in `sampleFields` on LOM chip rows. */
export const LOM_SAMPLE_FIELDS: Record<string, { field: string; label: string; decimal: boolean }[]> = {
  Gradient: [{ field: "gradient", label: `Gradient (${GRADIENT_UNIT})`, decimal: true }],
  Porosity: [{ field: "mi", label: "MI", decimal: false }, { field: "ma", label: "MA", decimal: false }],
};

/** True when any selected analysis needs sample preparation. */
export function orderNeedsPreparation(order: AnalysisOrder): boolean {
  return (order.selectedAnalysesJsonData?.selectedAnalyses ?? []).some(key => definitionNeedsPreparation(getAnalysisDefinition(key)));
}

/** Preparation methods that need a result section. Empty when preparation is skipped or no selected analysis needs it. */
export function getSelectedPreparationMethods(order: AnalysisOrder): string[] {
  const data = order.preparationAndRoutingJsonData;
  if (!orderNeedsPreparation(order) || data?.noPreparationRequired) return [];
  return (data?.selectedPreparationMethods ?? []).filter(Boolean);
}

/** True when sample preparation is needed by the selected analyses but the user chose "No preparation required". */
export function isPreparationSkipped(order: AnalysisOrder): boolean {
  return orderNeedsPreparation(order) && Boolean(order.preparationAndRoutingJsonData?.noPreparationRequired);
}

/** Analysis keys that get a result section. Mechanical preparation comes first, and only when preparation methods are chosen. */
export function getResultAnalysisKeys(order: AnalysisOrder): string[] {
  const selected = (order.selectedAnalysesJsonData?.selectedAnalyses ?? []).filter(key => !getAnalysisDefinition(key)?.preparationOnly);
  return getSelectedPreparationMethods(order).length > 0 ? [MECHANICAL_PREPARATION_KEY, ...selected] : selected;
}

export function selectedResultMeasurements(order: AnalysisOrder, key: string): string[] | null {
  if (key === MECHANICAL_PREPARATION_KEY) return getSelectedPreparationMethods(order);
  if (!["carbon", "hardness", "hcMmDensity", "oxygenNitrogen", ...CHIP_RESULT_ANALYSES].includes(key)) return null;
  const options = normalizePreparationRoutingItem(order.preparationAndRoutingJsonData?.routes?.[key]).selectedOptions;
  if (key === "carbon") return options.includes("C") ? ["Ctot", "Cfree"] : [];
  const allowed = getAnalysisDefinition(key)?.routeOptions ?? [];
  const clean = (value: string) => value.replace(/\*/g, "").trim();
  const selected = allowed.filter(option => options.some(selected => clean(selected) === clean(option))).map(clean);
  if (key === "hcMmDensity") return HC_MM_DENSITY_ROW_ORDER.filter(rowLabel => rowLabel === SIGMA_S_ROW || selected.includes(rowLabel));
  return selected;
}

export function selectResultSection(order: AnalysisOrder, section: AnalysisResultSection, sampleIds: string[]): AnalysisResultSection {
  const measurements = selectedResultMeasurements(order, section.analysisKey);
  if (measurements === null) return section;
  return { ...section, rows: measurements.map(rowLabel => section.rows.find(row => row.rowLabel === rowLabel) ?? {
    rowLabel, sampleValues: CHIP_RESULT_ANALYSES.includes(section.analysisKey) ? {} : Object.fromEntries(sampleIds.map(id => [id, ""])),
    files: [], comment: "",
  }) };
}

export function selectOrderResult(order: AnalysisOrder, data: AnalysisResultData, defaults: AnalysisResultSection[], sampleIds: string[]): AnalysisResultData {
  const sections = defaults.map(fallback => selectResultSection(order, data.sections.find(section => section.analysisKey === fallback.analysisKey) ?? fallback, sampleIds));
  const physical = sections.find(section => section.analysisKey === "hcMmDensity");
  const samples = normalizeSamples(order.linkedPowderOrderLabel || order.linkedPowderOrder, order.samples);
  const xrfCoRow = sections.find(section => section.analysisKey === "xrf")?.rows.find(row => row.rowLabel === "Co");
  const lomRows = sections.find(section => section.analysisKey === "lom")?.rows ?? [];
  const lomValue = (rowLabel: string, field: string, sampleId: string) => lomRows.find(row => row.rowLabel === rowLabel)?.sampleFields?.[field]?.[sampleId] ?? "";
  const summaryRows = sampleIds.map(sampleId => {
    const existing = data.summaryRows.find(row => row.sampleId === sampleId) ?? { sampleId };
    const values = Object.fromEntries(([['Hc', 'hc'], ['Density', 'density']] as const).map(([label, field]) => [field, physical?.rows.find(row => row.rowLabel === label)?.sampleValues[sampleId] ?? ""]));
    const orderCo = samples.find(sample => sample.sampleId === sampleId)?.coPercentage;
    const co = resolveCoContent(xrfCoRow?.sampleValues[sampleId], orderCo);
    const sigma = physical?.rows.find(row => row.rowLabel === SIGMA_S_ROW)?.sampleValues[sampleId] ?? "";
    return {
      ...existing,
      ...values,
      co: co?.displayValue ?? "",
      o: sigma,
      mi: lomValue("Porosity", "mi", sampleId),
      ma: lomValue("Porosity", "ma", sampleId),
      gradient: lomValue("Gradient", "gradient", sampleId),
      ...calculateMagneticResults(sigma, co),
    };
  });
  return { ...data, sections, summaryRows };
}
